import { DialogComponent } from "@theme/dialog";

/**
 * Global waitlist signup dialog. Listens for the `waitlist:open` event
 * dispatched by <add-to-cart-component> and submits the captured email
 * through Shopify's `{% form 'customer' %}` (Klaviyo picks it up from the
 * Shopify→Klaviyo sync, gated on the per-list tag).
 *
 * @typedef {object} WaitlistDialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLIFrameElement} resultFrame
 * @property {HTMLInputElement} tagsInput
 * @property {HTMLInputElement} noteInput
 * @property {HTMLInputElement} input
 * @property {HTMLElement} heading
 * @property {HTMLElement} bodyText
 * @property {HTMLElement} ctaText
 * @property {HTMLElement} messageSuccess
 *
 * @extends {DialogComponent}
 */
class WaitlistDialog extends DialogComponent {
  /** @type {boolean} */
  #pendingSubmit = false;

  /**
   * Translation defaults captured on connect. The dialog is global and reused
   * across products, so we restore these whenever a product has no override.
   * @type {{ heading: string, cta: string }}
   */
  #defaults = { heading: "", cta: "" };

  connectedCallback() {
    super.connectedCallback();

    this.form = this.querySelector("form");
    const { resultFrame, heading, ctaText } = this.refs;

    this.#defaults.heading = heading?.textContent ?? "";
    this.#defaults.cta = ctaText?.textContent ?? "";

    if (this.form && resultFrame) {
      this.form.target = resultFrame.getAttribute("name");
      this.form.addEventListener("submit", () => {
        this.#pendingSubmit = true;
      });
      resultFrame.addEventListener("load", this.#handleResult);
    }

    document.addEventListener("waitlist:open", this.#handleOpen);
  }

  disconnectedCallback() {
    document.removeEventListener("waitlist:open", this.#handleOpen);
    super.disconnectedCallback();
  }

  /**
   * @param {CustomEvent<{ listId: string; productHandle: string; productTitle: string; heading?: string; body?: string; cta?: string }>} event
   */
  #handleOpen = (event) => {
    const { listId, productHandle, productTitle, heading, body, cta } = event.detail || {};
    const { tagsInput, noteInput, heading: headingEl, bodyText, ctaText, input, messageSuccess } = this.refs;

    if (!listId) return;

    if (tagsInput) tagsInput.value = `waitlist,waitlist-${listId}`;
    if (noteInput) noteInput.value = productHandle ? `Product: ${productHandle}` : "";

    // Per-product overrides fall back to the translation defaults captured on connect.
    if (headingEl) headingEl.textContent = heading || this.#defaults.heading;
    if (ctaText) ctaText.textContent = cta || this.#defaults.cta;

    if (bodyText) {
      if (body) {
        // Product-specific copy is authored for one product, so use it verbatim.
        bodyText.textContent = body;
      } else {
        const template = bodyText.dataset.bodyTemplate || "";
        const fallback = bodyText.dataset.fallbackProduct || "this product";
        bodyText.textContent = template.replace("__PRODUCT__", productTitle || fallback);
      }
    }

    // Reset previous success state on re-open
    messageSuccess?.classList.add("hidden");
    if (input) input.value = "";

    this.showDialog();
  };

  #handleResult = () => {
    if (!this.#pendingSubmit) return;
    this.#pendingSubmit = false;

    const { input, messageSuccess } = this.refs;
    messageSuccess?.classList.remove("hidden");
    if (input) input.value = "";

    // Auto-close after a short delay so the shopper can read the confirmation.
    setTimeout(() => {
      this.closeDialog();
    }, 1800);
  };
}

if (!customElements.get("waitlist-dialog")) {
  customElements.define("waitlist-dialog", WaitlistDialog);
}
